import React, { useEffect, useState } from "react";
import { Eye, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useTurnstile, TurnstileFallback } from "../hooks/useTurnstile";

// Batch 2 §12 — Worker frontage via api.jjargent.com (custom domain).
// Set REACT_APP_WORKER_API in frontend/.env; falls back to the custom domain.
const WORKER_API = process.env.REACT_APP_WORKER_API || "https://api.jjargent.com/api";

// Tiny "eye" visitor counter — lives in the footer bottom bar
export function VisitorCounter() {
  const [count, setCount] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${WORKER_API}/visitors`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (typeof data?.count === "number") setCount(data.count);
        else setError(true);
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  return (
    <span
      data-testid="visitor-counter"
      className="inline-flex items-center gap-2 font-display normal-case tracking-[0.14em] text-[10px] text-[#A9A9A6]"
      aria-label="Visitor count"
    >
      <Eye size={12} className="text-[#D8A936]" aria-hidden />
      <span>
        {error ? "—" : count === null ? "…" : count.toLocaleString()}
        <span className="ml-1 text-[#6E6E6B]">visits</span>
      </span>
    </span>
  );
}

// Compact newsletter signup — footer column
export function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [message, setMessage] = useState("");
  const { containerRef: turnstileRef, token: turnstileToken, reset: resetTurnstile, scriptFailed: turnstileFailed } = useTurnstile();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!turnstileToken) {
      setStatus("error");
      setMessage("Please complete the bot check");
      toast.error("Please complete the bot check");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const res = await fetch(`${WORKER_API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), turnstileToken }),
      });
      const data = await res.json();
      if (data?.success) {
        setStatus("done");
        setMessage("Subscribed — thank you.");
        setEmail("");
        resetTurnstile();
      } else {
        setStatus("error");
        setMessage(data?.error || "Could not subscribe.");
      }
    } catch (err) {
      setStatus("error");
      setMessage("Network error. Try again shortly.");
    }
  };

  return (
    <form onSubmit={submit} data-testid="newsletter-form" className="mt-4 space-y-2">
      <label htmlFor="footer-newsletter-email" className="sr-only">Email for newsletter</label>
      <div className="flex items-stretch border border-[#343437] bg-[#151517] focus-within:border-[#D8A936] transition-colors">
        <input
          id="footer-newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === "loading"}
          data-testid="newsletter-email"
          placeholder="you@work.com"
          className="flex-1 bg-transparent text-[#FAF9F6] text-sm px-3 py-2.5 outline-none placeholder:text-[#6E6E6B]"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid="newsletter-submit"
          aria-label="Subscribe"
          className="px-4 bg-[#D8A936] text-[#0B0B0C] hover:bg-[#FAF9F6] transition-colors disabled:opacity-60"
        >
          {status === "loading" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </div>
      <div ref={turnstileRef} data-testid="newsletter-turnstile" />
      <TurnstileFallback show={turnstileFailed} />
      {status === "done" && (
        <p className="text-[10px] tracking-[0.14em] uppercase text-[#4ADE80]" data-testid="newsletter-success">
          {message}
        </p>
      )}
      {status === "error" && (
        <p className="text-[10px] tracking-[0.14em] uppercase text-[#EF4444]" data-testid="newsletter-error">
          {message}
        </p>
      )}
    </form>
  );
}
